import cx from 'classnames';
import * as _ from 'lodash';
import * as React from 'react';

import { VulnSeverity } from '../../../../shared/issuesV2';
import { LICENSING_ISSUE_CONFIG, QUALITY_ISSUE_CONFIG } from '../../../shared/issuesV2/issueConfig';
import { severityToBadgeColor } from '../VulnBadge/utils';
import type {
  IssueRevisionSeverity,
  IssueRevisionType,
  LicensingIssueType,
  QualityIssueType,
} from '../../../../shared/issuesV2';

import TooltipBadge from './TooltipBadge';
import type { AlertColor, BadgeColor, BadgeProps, VulnBadgeColor } from './Badge';

import css from './IssueCountBadge.module.less';

/**
 * A partition is a set of issues that show up under one label. Each partition
 * gets a dot in the badge and a row in the tooltip.
 */
type Partition = {
  label: string;
  count: number;
  color: BadgeColor | VulnBadgeColor | AlertColor;
};

/** A template used to define partitions. */
type PartitionTemplate = {
  key: VulnSeverity | QualityIssueType | LicensingIssueType;
  label: string;
  color: BadgeColor | VulnBadgeColor | AlertColor;
};

export type IssueCountBadgeProps = {
  id: string;
  pluralLabel?: (count: number) => string;
  issueTypes: Partial<IssueRevisionType & IssueRevisionSeverity>;
} & Omit<BadgeProps, 'children'>;

const defaultPluralLabel = () => '';

/** There are the templates for partitions _in the order they will appear_. */
const partitionTemplates = [
  ...(Object.values(VulnSeverity) as VulnSeverity[]).map(severity => ({
    key: severity,
    label: _.capitalize(severity),
    color: severityToBadgeColor(severity),
  })),
  ...Object.entries(LICENSING_ISSUE_CONFIG).map(([key, { label, color }]) => ({
    key,
    label,
    color,
  })),
  ...Object.entries(QUALITY_ISSUE_CONFIG).map(([key, { label, color }]) => ({
    key,
    label,
    color,
  })),
] as PartitionTemplate[];

/** Extract from the revision the partitions that we are going to show. */
function getPartitions(types: Partial<IssueRevisionType & IssueRevisionSeverity>): Partition[] {
  return partitionTemplates.flatMap(({ key, label, color }) =>
    types[key]
      ? [{
          label,
          count: types[key],
          color,
        }]
      : [],
  );
}

/**
 * A badge showing the total number of issues for a revision. It has a tooltip
 * with details and little dots indicating the categories that are present.
 */
const IssueCountBadge: React.VFC<IssueCountBadgeProps> = ({
  issueTypes,
  id,
  pluralLabel = defaultPluralLabel,
  ...badgeProps
}) => {
  const partitions = React.useMemo(() => getPartitions(issueTypes), [issueTypes]);

  const totalCount = Object.values(issueTypes).reduce((count, n) => count + n, 0);

  const tooltipContent = (
    <div className={css.tooltip}>
      {partitions.map((partition) => (
        <div key={partition.label}>
          {partition.count} {partition.label}
        </div>
      ))}
    </div>
  );

  return (
    <TooltipBadge id={`IssueCountBadgeTooltip-${id}`} tooltip={tooltipContent} {...badgeProps}>
      <div className={css.badgeInner}>
        <div className={css.badgeLabel}>
          <span className={css.totalCount}>{totalCount}</span>
          <span>{ pluralLabel(totalCount) }</span>
        </div>
        <div className={css.dots}>
          {
            partitions.map((partition) => (
              <div
                key={partition.label}
                className={cx(css.dot, css[`dot-${partition.color}`])}
                />
            ))
          }
        </div>
      </div>
    </TooltipBadge>
  );
};

export default IssueCountBadge;
